import Axios from 'api/client';
import { ContactType } from 'constants/contacts';
import {
    Contact,
    ContactEoyReport,
    EngageMessageChannel,
    FirstTimeSupporter,
    Transaction,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface FetchContactsParams extends FilterParams {
    isArchived?: boolean;
    messageId?: number;
    isBundle?: boolean;
    messageIdNoSavedFilters?: number;
    channel?: EngageMessageChannel;
    type?: ContactType;
    nameRequired?: boolean;
    hasPrimaryEmail?: string;
    isEmailSubscribed?: boolean;
    hasPrimaryPhone?: string;
    isPhoneSubscribed?: boolean;
}

interface BulkDeleteContactsResponse {
    contact_ids_not_deleted: number[];
    message: string;
    number_of_contacts_deleted: number;
    number_of_contacts_not_deleted: number;
}

interface FetchEoyReportsResponse {
    eoy_reports: ContactEoyReport[];
}

const fetchContacts = (accountId: number, params: FetchContactsParams = {}) => {
    const {
        filters,
        isArchived,
        messageId,
        eager = false,
        include,
        nameRequired,
        hasPrimaryEmail,
        isEmailSubscribed,
        hasPrimaryPhone,
        isPhoneSubscribed,
        ...restParams
    } = params;

    const eagerParams = eager ? 'customFieldResponses,primaryAddress' : '';
    const includeParams = [eagerParams, include ?? ''].filter(Boolean).join(',');

    return Axios.post<PaginatedData<Contact>>(`/accounts/${accountId}/get_contacts`, {
        archived: isArchived ? 1 : 0,
        message: messageId,
        include: includeParams,
        name_required: nameRequired,
        has_primary_email: hasPrimaryEmail,
        is_email_subscribed: isEmailSubscribed,
        has_primary_phone: hasPrimaryPhone,
        is_phone_subscribed: isPhoneSubscribed,
        ...filters,
        ...restParams,
    }).then(response => response.data);
};

interface ContactGivingSummaryChartResponse {
    type: 'year_to_date' | 'all_time' | 'last_year';
    transactions: Array<Transaction>;
    aggregates_by_date_range: {
        total: number;
        largest_donation: null | Transaction;
        recurring_donation: null | Transaction;
        top_donor: boolean;
        donor_percentile: number;
        recurring_donations_sum: number;
        soft_credits_sum: number;
    };
}

const fetchContactGivingSummaryChartStats = (
    accountId: number,
    contactId: number,
    type: string,
    contact_type: ContactType,
) => {
    return Axios.get<ContactGivingSummaryChartResponse>(
        `/accounts/${accountId}/metrics/contact-giving-summary`,
        {
            params: {
                contact_ids: [contactId],
                type,
                contact_type,
            },
        },
    ).then(response => response.data);
};

const getFirstTimeSupporters = (accountId: number, params: { limit?: number; page?: number }) =>
    Axios.get<PaginatedData<FirstTimeSupporter>>(`accounts/${accountId}/first-time-supporters`, {
        params,
    }).then(response => response.data);

const deleteFirstTimeSupporter = (accountId: number, contactId: number) =>
    Axios.delete(`accounts/${accountId}/first-time-supporters/${contactId}`).then(
        response => response.data,
    );

const mergeContact = (accountId: number, fromContactId: number, intoContactId: number) =>
    Axios.post<Contact>(`/accounts/${accountId}/contacts/${fromContactId}/merge`, {
        contact_id: intoContactId,
    }).then(response => response.data);

interface UpdateContactsSubscriptionsParams {
    is_email_subscribed: boolean | null;
    is_phone_subscribed: boolean | null;
    is_address_subscribed: boolean | null;
    emails: (string | null)[];
    phones: (string | null)[];
    contact_ids: number[];
}

const updateContactsSubscriptions = (
    accountId: number,
    params: UpdateContactsSubscriptionsParams,
) =>
    Axios.post(`/accounts/${accountId}/contact_subscriptions`, params).then(
        response => response.data,
    );

const deleteContacts = (accountId: number, contactIds: number[]) =>
    Axios.delete<BulkDeleteContactsResponse>(`/accounts/${accountId}/contacts/bulk-delete`, {
        data: { contact_ids: contactIds },
    }).then(response => response.data);

const fetchEoyReports = (accountId: number, contactId: number) =>
    Axios.get<FetchEoyReportsResponse>(
        `/accounts/${accountId}/contacts/${contactId}/eoy-reports`,
    ).then(response => response.data);

export {
    fetchContacts,
    fetchContactGivingSummaryChartStats,
    getFirstTimeSupporters,
    deleteFirstTimeSupporter,
    mergeContact,
    updateContactsSubscriptions,
    deleteContacts,
    fetchEoyReports,
};
export type {
    FetchContactsParams,
    ContactGivingSummaryChartResponse,
    UpdateContactsSubscriptionsParams,
};
